import React, { useEffect, useRef } from 'react';

interface MotionBackgroundProps {
  variant?: 'hero' | 'section' | 'subtle';
  showOrbs?: boolean;
  showWaves?: boolean;
  showMesh?: boolean;
  className?: string;
}

export const MotionBackground: React.FC<MotionBackgroundProps> = ({
  variant = 'hero',
  showOrbs = true,
  showWaves = true,
  showMesh = true,
  className = ''
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleMouseMove = (e: MouseEvent) => {
      const rect = container.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      const y = (e.clientY - rect.top) / rect.height;

      // Apply subtle parallax effect to orbs based on mouse position
      const orbs = container.querySelectorAll('.floating-orb');
      orbs.forEach((orb, index) => {
        const element = orb as HTMLElement;
        const speed = (index + 1) * 0.02;
        const moveX = (x - 0.5) * speed * 50;
        const moveY = (y - 0.5) * speed * 50;
        
        element.style.transform = `translate(${moveX}px, ${moveY}px)`;
      });
    };

    container.addEventListener('mousemove', handleMouseMove);
    
    return () => {
      container.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  const getBackgroundClass = () => {
    switch (variant) {
      case 'hero':
        return 'bg-animated-hero';
      case 'section':
        return 'bg-animated-section';
      case 'subtle':
        return 'bg-animated-subtle';
      default:
        return 'bg-animated-hero';
    }
  };

  return (
    <div 
      ref={containerRef}
      className={`
        fixed inset-0 -z-10 overflow-hidden
        ${getBackgroundClass()}
        ${showMesh ? 'mesh-gradient' : ''}
        ${className}
      `}
    >
      {/* Floating Orbs */}
      {showOrbs && (
        <>
          <div className="floating-orb floating-orb-1" />
          <div className="floating-orb floating-orb-2" />
          <div className="floating-orb floating-orb-3" />
          
          {/* Additional orbs for more dynamic effect */}
          <div 
            className="floating-orb"
            style={{
              position: 'absolute',
              top: '40%',
              right: '30%',
              width: '120px',
              height: '120px',
              background: 'radial-gradient(circle, rgba(255, 140, 0, 0.12) 0%, transparent 70%)',
              borderRadius: '50%',
              animation: 'float-2 22s ease-in-out infinite',
              filter: 'blur(2px)',
              animationDelay: '5s'
            }}
          />
          
          <div 
            className="floating-orb"
            style={{
              position: 'absolute',
              top: '80%',
              left: '20%',
              width: '180px',
              height: '180px',
              background: 'radial-gradient(circle, rgba(255, 140, 0, 0.06) 0%, transparent 70%)',
              borderRadius: '50%',
              animation: 'float-3 28s ease-in-out infinite reverse',
              filter: 'blur(4px)',
              animationDelay: '8s'
            }}
          />
        </>
      )}

      {/* Wave Lines */}
      {showWaves && (
        <div className="wave-lines">
          <div className="wave-line wave-line-1" />
          <div className="wave-line wave-line-2" />
          <div className="wave-line wave-line-3" />
          
          {/* Additional wave lines */}
          <div 
            className="wave-line"
            style={{
              position: 'absolute',
              top: '25%',
              left: '-100%',
              width: '90%',
              height: '1px',
              background: 'linear-gradient(90deg, transparent, rgba(255, 140, 0, 0.2), transparent)',
              animation: 'wave-slide-1 12s ease-in-out infinite',
              animationDelay: '6s'
            }}
          />
          
          <div 
            className="wave-line"
            style={{
              position: 'absolute',
              top: '65%',
              right: '-100%',
              width: '110%',
              height: '1px',
              background: 'linear-gradient(90deg, transparent, rgba(255, 140, 0, 0.15), transparent)',
              animation: 'wave-slide-2 14s ease-in-out infinite',
              animationDelay: '3s'
            }}
          />
        </div>
      )}

      {/* Scanlines Effect */}
      <div className="scanlines opacity-30" />
      
      {/* Gradient Overlay for Better Content Contrast */}
      <div 
        className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-background/20"
        style={{ pointerEvents: 'none' }}
      />
    </div>
  );
};